//Dependencies
import React, { useEffect, useMemo, useState } from 'react';
import { FiDownload, FiGift, FiSearch, FiUsers, FiUserPlus, FiActivity, FiStar, FiMail, FiCopy, FiX } from 'react-icons/fi';
import { PageHeader, Kpi, Badge, Notice, Modal, Skeleton, DateRange } from '../Components/UI';
import DataTable, { Column } from '../Components/UI/DataTable';
import Pagination from '../Components/UI/Pagination';
import Drawer from '../Components/UI/Drawer';
import { useUrlState } from '../Components/Hooks/useUrlState';
import { useDebounce } from '../Components/Hooks/useDebounce';
import { useCachedQuery, prefetchQuery } from '../Components/Hooks/useCachedQuery';
import {
  fetchWorksheetUsers,
  fetchWorksheetStats,
  fetchWorksheetUser,
  downloadWorksheetExport,
  giveUserPaidSub,
  WorksheetUserQuery,
} from '../Utils/WorksheetUsersAPI';
import { SignupSource, WorksheetUserRow } from '../Models/AdminUsers';
import { formatDate, formatDateTime, formatNumber, initialsOf, timeAgo, withAgo } from '../Utils/Format';
import { ValidateEmail } from '../Utils';
import { SignupSiteBadge, SignupSiteCell } from '../Components/Users/SignupSite';
import { AccessBadge } from '../Components/Users/AccessBadge';

const DEFAULTS = {
  page: '1',
  limit: '25',
  search: '',
  activity: 'all',
  plan: 'all',
  site: 'all',
  from: '',
  to: '',
  sort: 'created',
  dir: 'desc',
  user: '',
};

const PLAN_LABELS: Record<string, string> = { paid: 'Paid', tutor: 'Tutor', client: 'Client', free: 'Free' };

export const PlanBadge: React.FC<{ plan: string }> = ({ plan }) => (
  <Badge tone={PLAN_LABELS[plan] ? plan : 'free'}>{PLAN_LABELS[plan] || plan || 'Free'}</Badge>
);

const WorksheetsUsers: React.FC = () => {
  const [state, setState] = useUrlState(DEFAULTS);
  const [searchInput, setSearchInput] = useState(state.search);
  const debouncedSearch = useDebounce(searchInput.trim(), 300);
  const [giveSubFor, setGiveSubFor] = useState<string | null>(null);

  // Typing updates the URL after the debounce and always returns to page 1.
  useEffect(() => {
    if (debouncedSearch !== state.search) setState({ search: debouncedSearch, page: '1' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  useEffect(() => {
    if (state.search !== searchInput.trim()) setSearchInput(state.search);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.search]);

  const query: WorksheetUserQuery = useMemo(
    () => ({
      page: Math.max(1, parseInt(state.page, 10) || 1),
      limit: Math.min(100, Math.max(10, parseInt(state.limit, 10) || 25)),
      search: state.search,
      activity: state.activity as WorksheetUserQuery['activity'],
      plan: state.plan,
      from: state.from,
      to: state.to,
      sort: state.sort,
      dir: state.dir === 'asc' ? 'asc' : 'desc',
      site: state.site,
    }),
    [state.page, state.limit, state.search, state.activity, state.plan, state.site, state.from, state.to, state.sort, state.dir],
  );
  const key = `ws:list:${JSON.stringify(query)}`;
  const list = useCachedQuery(key, (signal) => fetchWorksheetUsers(query, signal));
  const stats = useCachedQuery('ws:stats', (signal) => fetchWorksheetStats(signal), 60000);
  const legacy = !!(list.data && list.data.legacy);

  // Warm the next page so "Next" is instant.
  useEffect(() => {
    if (!list.data || list.loading || query.page >= list.data.totalPages) return;
    const next = { ...query, page: query.page + 1 };
    prefetchQuery(`ws:list:${JSON.stringify(next)}`, (signal) => fetchWorksheetUsers(next, signal));
  }, [list.data, list.loading, query]);

  // The legacy endpoint has no "all" view: default it to users with downloads.
  useEffect(() => {
    if (legacy && state.activity === 'all') setState({ activity: 'with', page: '1' });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [legacy]);

  const filtersActive = !!(state.search || state.plan !== 'all' || state.site !== 'all' || state.from || state.to || state.activity !== 'all');
  const clearFilters = () => {
    setSearchInput('');
    setState({ search: '', plan: 'all', site: 'all', from: '', to: '', activity: legacy ? 'with' : 'all', page: '1' });
  };

  const onSort = (sortKey: string) => {
    if (state.sort === sortKey) setState({ dir: state.dir === 'desc' ? 'asc' : 'desc', page: '1' });
    else setState({ sort: sortKey, dir: sortKey === 'email' ? 'asc' : 'desc', page: '1' });
  };

  const columns: Column<WorksheetUserRow>[] = [
    {
      key: 'user',
      header: 'User',
      sortKey: legacy ? undefined : 'email',
      render: (user) => (
        <div className="cell-user">
          <span className="avatar">{initialsOf(user.name, user.email)}</span>
          <div style={{ minWidth: 0 }}>
            <strong>{user.name && user.name.trim() ? user.name : user.email}</strong>
            <span>{user.name && user.name.trim() ? user.email : user.phone || 'No name provided'}</span>
          </div>
        </div>
      ),
      skeleton: (
        <div className="cell-user">
          <Skeleton circle width="3.4rem" height="3.4rem" />
          <div style={{ flex: 1 }}>
            <Skeleton width="14rem" block />
            <div style={{ height: '0.6rem' }} />
            <Skeleton width="20rem" block />
          </div>
        </div>
      ),
    },
    { key: 'site', header: 'Signed up on', render: (user) => <SignupSiteCell site={user.signupSite} alsoOnOther={user.alsoOnOtherSite} other="readers" /> },
    { key: 'plan', header: 'Plan', render: (user) => <AccessBadge plan={user.plan} paying={user.paying} /> },
    { key: 'role', header: 'Role', render: (user) => user.role || <span className="muted">Incomplete</span> },
    {
      key: 'downloads',
      header: 'Downloads',
      className: 'num',
      sortKey: legacy ? undefined : 'downloads',
      render: (user) => formatNumber(user.worksheetCount + user.assessmentCount),
    },
    {
      key: 'activity',
      header: 'Last download',
      sortKey: legacy ? undefined : 'activity',
      render: (user) =>
        user.lastActivity ? <span title={formatDateTime(user.lastActivity)}>{timeAgo(user.lastActivity)}</span> : <span className="muted">Never</span>,
    },
    {
      key: 'created',
      header: 'Signed up',
      sortKey: legacy ? undefined : 'created',
      render: (user) => <span title={formatDateTime(user.createdAt)}>{formatDate(user.createdAt)}</span>,
    },
  ];

  const s = stats.data;
  const weekAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().slice(0, 10);
  const CLEARED = { search: '', plan: 'all', site: 'all', from: '', to: '', activity: 'all', page: '1' };
  const showCard = (filters: Record<string, string>) => {
    setSearchInput('');
    setState({ ...CLEARED, ...filters });
    setTimeout(() => document.querySelector('.table-card')?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 50);
  };
  const cardActive = (filters: Record<string, string>) =>
    Object.keys(CLEARED).every((key) => (key in filters ? state[key as keyof typeof state] === filters[key] : key === 'page' || state[key as keyof typeof state] === (CLEARED as any)[key]));
  const [exporting, setExporting] = useState(false);
  const runExport = () => {
    setExporting(true);
    downloadWorksheetExport({ ...query })
      .catch((err) => window.alert(err.message))
      .finally(() => setExporting(false));
  };

  return (
    <div>
      <PageHeader
        title="Worksheet users"
        description="Everyone with a No Fuss Tutors worksheets account. Search, filter and click a row for details."
        actions={
          <>
            <button className="btn--secondary" onClick={() => setGiveSubFor('')}>
              <FiGift /> Give paid sub
            </button>
            {!legacy && (
              <button type="button" className="btn btn--secondary" onClick={runExport} disabled={exporting}>
                <FiDownload /> {exporting ? 'Exporting…' : 'Export CSV'}
              </button>
            )}
          </>
        }
      />

      <div className="grid grid--kpi" style={{ marginBottom: '2rem' }}>
        <Kpi label="Total users" onClick={() => showCard({})} active={cardActive({})} icon={<FiUsers />} value={formatNumber(s && s.total)} loading={stats.loading && !s} sub={s && s.signedUpHere !== undefined && s.siteNotRecorded === 0 ? `${formatNumber(s.signedUpHere)} signed up here, ${formatNumber(s.fromReaders)} came from Readers` : s && s.newLast30 !== undefined ? `${formatNumber(s.newLast30)} joined in the last 30 days` : ' '} />
        <Kpi
          label="New this week"
          onClick={() => showCard({ from: weekAgo })}
          active={cardActive({ from: weekAgo })}
          icon={<FiUserPlus />}
          tone="coral"
          value={formatNumber(s && s.newLast7)}
          loading={stats.loading && !s}
          sub="Sign ups in the last 7 days"
        />
        <Kpi
          label="Downloaded worksheets"
          onClick={() => showCard({ activity: 'with' })}
          active={cardActive({ activity: 'with' })}
          icon={<FiActivity />}
          tone="green"
          value={formatNumber(s && s.withActivity)}
          loading={stats.loading && !s}
          sub={s && s.activeLast30 !== undefined ? `${formatNumber(s.activeLast30)} active in the last 30 days` : 'At least one download'}
        />
        <Kpi
          label="Paying (Stripe)"
          onClick={() => showCard({ plan: 'paying' })}
          active={cardActive({ plan: 'paying' })}
          icon={<FiStar />}
          tone="amber"
          value={formatNumber(s ? (s.payingStripe ?? s.paid) : undefined)}
          loading={stats.loading && !s}
          sub={s && s.freeAccess !== undefined && s.freeAccess !== null ? `${formatNumber(s.freeAccess)} have free access (tutors, clients)` : s && s.client !== undefined ? `${formatNumber(s.client)} client and ${formatNumber(s.tutor)} tutor accounts` : ' '}
        />
      </div>

      {legacy && (
        <Notice style={{ marginBottom: '1.6rem' }}>
          The server update for fast search and filters is not deployed yet, so this list uses the older endpoint
          (paged, newest activity first, no search). Everything else works.
        </Notice>
      )}
      {list.error && (
        <Notice tone="error" style={{ marginBottom: '1.6rem' }}>
          Could not load users: {list.error.message}{' '}
          <button className="btn--sm btn--secondary" onClick={list.reload}>
            Try again
          </button>
        </Notice>
      )}

      <div className="table-card">
        <div className="toolbar">
          <div className="input-icon">
            <FiSearch />
            <input
              type="search"
              placeholder={legacy ? 'Search needs the server update' : 'Search by email or name'}
              value={searchInput}
              disabled={legacy}
              onChange={(event) => setSearchInput(event.target.value)}
              aria-label="Search users"
            />
          </div>
          <div className="chips" role="group" aria-label="Downloads">
            {(legacy ? ['with', 'without'] : ['all', 'with', 'without']).map((value) => (
              <button
                key={value}
                className={state.activity === value ? 'is-active' : ''}
                onClick={() => setState({ activity: value, page: '1' })}
              >
                {value === 'all' ? 'All' : value === 'with' ? 'Downloaded' : 'No downloads'}
                {s && value === 'with' && s.withActivity !== undefined && <span className="count">{formatNumber(s.withActivity)}</span>}
                {s && value === 'without' && s.withoutActivity !== undefined && <span className="count">{formatNumber(s.withoutActivity)}</span>}
              </button>
            ))}
          </div>
          <select value={state.plan} onChange={(event) => setState({ plan: event.target.value, page: '1' })} disabled={legacy} aria-label="Plan">
            <option value="all">All plans</option>
            <option value="paying">Paying (Stripe)</option>
            <option value="paid">Marked paid</option>
            <option value="client">Client</option>
            <option value="tutor">Tutor</option>
            <option value="free">Free</option>
          </select>
          <select value={state.site} onChange={(event) => setState({ site: event.target.value, page: '1' })} disabled={legacy} aria-label="Signed up on">
            <option value="all">Signed up anywhere</option>
            <option value="worksheets">Signed up on worksheets</option>
            <option value="readers">Came from Readers</option>
          </select>
          <DateRange from={state.from} to={state.to} disabled={legacy} onChange={(range) => setState({ ...range, page: '1' })} />
          {filtersActive && (
            <button className="btn--ghost btn--sm" onClick={clearFilters}>
              <FiX /> Clear
            </button>
          )}
        </div>
        <div className={`progress-bar${list.loading && list.data ? ' is-active' : ''}`} />
        <DataTable
          columns={columns}
          rows={list.data ? list.data.users : []}
          rowKey={(user) => user._id}
          loading={list.loading}
          initialLoading={list.loading && !list.data}
          sort={state.sort}
          dir={state.dir as 'asc' | 'desc'}
          onSort={onSort}
          onRowClick={(user) => setState({ user: user._id })}
          selectedKey={state.user || null}
          skeletonRows={Math.min(query.limit, 12)}
          emptyTitle={filtersActive ? 'No users match these filters' : 'No users yet'}
          emptyBody={
            filtersActive ? (
              <button className="btn--secondary btn--sm" onClick={clearFilters}>
                Clear filters
              </button>
            ) : null
          }
        />
        <Pagination
          page={query.page}
          totalPages={list.data ? list.data.totalPages : 1}
          total={list.data ? list.data.total : 0}
          limit={query.limit}
          loading={list.loading}
          onPage={(page) => setState({ page: String(page) })}
          onLimit={(limit) => setState({ limit: String(limit), page: '1' })}
        />
      </div>

      {state.user && (
        <WorksheetUserDrawer
          id={state.user}
          preview={list.data ? list.data.users.find((user) => user._id === state.user) : undefined}
          onClose={() => setState({ user: '' })}
          onGiveSub={(email) => setGiveSubFor(email)}
        />
      )}
      {giveSubFor !== null && <GiveSubModal initialEmail={giveSubFor} onClose={() => setGiveSubFor(null)} />}
    </div>
  );
};

// Where the account came from (recorded at sign up from Nov 2026 on).
const describeOrigin = (source?: SignupSource | null) => {
  if (!source) return 'Not recorded';
  const via = source.utm_source
    ? [source.utm_source, source.utm_medium, source.utm_campaign].filter(Boolean).join(' / ')
    : source.referrer
    ? source.referrer.replace(/^www\./, '')
    : 'Direct or unknown';
  return source.landing ? `${via}, landed on ${source.landing}` : via;
};

const TRIGGERS: { [key: string]: string } = {
  header: 'Sign up button in the header',
  worksheet: 'Worksheet download',
};

const describeSignupPage = (source?: SignupSource | null) => {
  if (!source) return 'Not recorded';
  const parts = [source.signupPage || ''];
  if (source.worksheetTitle || source.worksheetId) parts.push(`wanted "${source.worksheetTitle || source.worksheetId}"`);
  else if (source.trigger) parts.push(TRIGGERS[source.trigger] || source.trigger);
  return parts.filter(Boolean).join(', ') || 'Not recorded';
};

const WorksheetUserDrawer: React.FC<{
  id: string;
  preview?: WorksheetUserRow;
  onClose: () => void;
  onGiveSub: (email: string) => void;
}> = ({ id, preview, onClose, onGiveSub }) => {
  const detail = useCachedQuery(`ws:user:${id}`, (signal) => fetchWorksheetUser(id, signal), 60000);
  const user = detail.data || preview;
  const [copied, setCopied] = useState(false);

  const copy = async () => {
    if (!user) return;
    try {
      await navigator.clipboard.writeText(user.email);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      setCopied(false);
    }
  };

  return (
    <Drawer
      title={user ? (user.name && user.name.trim()) || user.email : <Skeleton width="20rem" height="2rem" />}
      subtitle={user && user.name ? user.email : undefined}
      avatar={<span className="avatar">{user ? initialsOf(user.name, user.email) : ''}</span>}
      onClose={onClose}
    >
      {user && (
        <div style={{ display: 'flex', gap: '0.8rem', flexWrap: 'wrap', marginBottom: '2.4rem' }}>
          <a className="btn btn--secondary btn--sm" href={`mailto:${user.email}`}>
            <FiMail /> Email
          </a>
          <button className="btn--secondary btn--sm" onClick={copy}>
            <FiCopy /> {copied ? 'Copied' : 'Copy email'}
          </button>
          {user.plan !== 'paid' && user.plan !== 'client' && (
            <button className="btn--secondary btn--sm" onClick={() => onGiveSub(user.email)}>
              <FiGift /> Give paid sub
            </button>
          )}
        </div>
      )}
      <div className="drawer__section">
        <h3>Account</h3>
        {user ? (
          <dl className="kv">
            <dt>Plan</dt>
            <dd>
              <PlanBadge plan={user.plan} />
            </dd>
            <dt>Role</dt>
            <dd>{user.role || 'Incomplete'}</dd>
            <dt>Phone</dt>
            <dd>{user.phone || '–'}</dd>
            <dt>Tutor interest</dt>
            <dd>{user.tutorInterest || '–'}</dd>
            <dt>Signed up on</dt>
            <dd><SignupSiteBadge site={user.signupSite} /></dd>
            <dt>Signed up</dt>
            <dd>{formatDateTime(user.createdAt)}</dd>
            {detail.data && (
              <>
                <dt>Came from</dt>
                <dd>{describeOrigin(detail.data.signupSource)}</dd>
                <dt>Signed up from</dt>
                <dd>{describeSignupPage(detail.data.signupSource)}</dd>
              </>
            )}
            <dt>Last download</dt>
            <dd>{user.lastActivity ? withAgo(user.lastActivity) : 'Never'}</dd>
            <dt>Downloads</dt>
            <dd>
              {formatNumber(user.worksheetCount)} worksheets, {formatNumber(user.assessmentCount)} assessments
            </dd>
          </dl>
        ) : (
          <Skeleton width="100%" height="12rem" block />
        )}
      </div>
      <div className="drawer__section">
        <h3>Download history</h3>
        {detail.error && <Notice tone="error">Could not load downloads: {detail.error.message}</Notice>}
        {!detail.data && !detail.error && (
          <div>
            {[0, 1, 2].map((i) => (
              <div key={i} style={{ padding: '1rem 0' }}>
                <Skeleton width={`${70 - i * 10}%`} />
              </div>
            ))}
          </div>
        )}
        {detail.data && detail.data.downloads.length === 0 && <p className="muted">No downloads yet.</p>}
        {detail.data && detail.data.downloads.length > 0 && (
          <ul className="timeline">
            {detail.data.downloads.map((doc, index) => (
              <li key={`${doc.resourceId}-${index}`}>
                <span className={`timeline__dot${doc.type === 'assessment' ? ' timeline__dot--coral' : ''}`} />
                <div>
                  <div className="timeline__title">{doc.title || 'Untitled or removed resource'}</div>
                  <div className="timeline__meta">
                    {doc.type === 'assessment' ? 'Assessment' : 'Worksheet'} · {formatDateTime(doc.date)}
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </Drawer>
  );
};

const GiveSubModal: React.FC<{ initialEmail: string; onClose: () => void }> = ({ initialEmail, onClose }) => {
  const [email, setEmail] = useState(initialEmail);
  const [status, setStatus] = useState<{ tone: 'error' | 'success'; message: string } | null>(null);
  const [busy, setBusy] = useState(false);

  const submit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!ValidateEmail(email.trim())) {
      setStatus({ tone: 'error', message: 'Enter a valid email address.' });
      return;
    }
    setBusy(true);
    try {
      const response = await giveUserPaidSub(email.trim());
      setStatus({ tone: 'success', message: response.message });
    } catch (err) {
      setStatus({ tone: 'error', message: (err as Error).message });
    }
    setBusy(false);
  };

  return (
    <Modal title="Give a paid subscription" onClose={onClose}>
      <p>Gives a worksheets account free access to paid worksheets. The user must already have an account.</p>
      <form onSubmit={submit} style={{ marginTop: '1.6rem' }}>
        <label className="field">
          <span>Account email</span>
          <input type="email" value={email} onChange={(event) => setEmail(event.target.value)} placeholder="parent@example.com" autoFocus />
        </label>
        {status && (
          <Notice tone={status.tone} style={{ marginTop: '1.4rem' }}>
            {status.message}
          </Notice>
        )}
        <div className="ui-modal__actions">
          <button type="button" className="btn--secondary" onClick={onClose}>
            {status && status.tone === 'success' ? 'Done' : 'Cancel'}
          </button>
          <button type="submit" className="btn--coral" disabled={busy}>
            {busy ? 'Saving…' : 'Give paid sub'}
          </button>
        </div>
      </form>
    </Modal>
  );
};

export default WorksheetsUsers;
